import React, { useState, useEffect, useContext, useRef } from "react";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import {
  CheckSquare,
  CheckCircle2,
  Copy,
  Edit3,
  Info,
  Lightbulb,
  Loader2,
  Plus,
  Sparkles,
  X,
  Bell,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import { Textarea } from "../../../ui/textarea";
import { useNotes } from "../../chatPanel/takeaways/use-notes";
import { useTranslation } from "react-i18next";
import { LocationObject } from "../../../../lib/types";
import CheckpointReminderModal from "./CheckpointReminderModal";
import { MinimalEditor } from "../../../reactflow/infoPopover/MinimalEditor";
import { config } from "../../../../Environment";
import LoggerContext from "../../../../context/LoggerProvider";
import useAuth from "../../../../hooks/useAuth";
import type { PaneRecordings } from "../../../reactflow/infoPopover/MinimalEditor";
import convertPrescription from "../../../reactflow/convert_prescription";
import { convertOldCustomToNew } from "../../../../lib/utils";
import UnifiedExtractorDialog from "../../../shared/AIExtractorDialog";
import type { ExtractedItem } from "../../../shared/AIExtractorDialog/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";

const ActivityCompletionModal = ({
  isDialogOpen,
  setIsDialogOpen,
  isUserSignedIn = true,
  processId,
  forwardLocation,
  activityTitle,
}: {
  isDialogOpen: boolean;
  setIsDialogOpen: (arg0: boolean) => void;
  isUserSignedIn: boolean;
  processId: string;
  forwardLocation: LocationObject;
  activityTitle?: string;
}) => {
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [newNoteType, setNewNoteType] = useState<"action" | "insight">(
    "action",
  );
  const [newNoteContent, setNewNoteContent] = useState("");
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(
    null,
  );
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [epilogue, setEpilogue] = useState("");
  const prevEpilogueRef = useRef("");
  const [epilogueRecordings, setEpilogueRecordings] = useState<PaneRecordings>(
    {},
  );
  const [savingEpilogue, setSavingEpilogue] = useState(false);
  const [isMarkedAsExample, setIsMarkedAsExample] = useState(false);
  const [formattedUserData, setFormattedUserData] = useState("");
  const [showExtractorDialog, setShowExtractorDialog] = useState(false);
  const navigate = useNavigate();

  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { Logger } = useContext(LoggerContext);
  const { user: currentUser } = useAuth();
  const [isActivityAuthor, setIsActivityAuthor] = useState(false);
  const [checkingAuthor, setCheckingAuthor] = useState(true);

  const { notes, loading, addNote, editNote, removeNote } = useNotes({
    processId,
    onError: (error: any) => {
      toast(error);
    },
    onSuccess: (message: any) => {
      toast(message);
    },
  });

  // Load existing epilogue and check if user is author when dialog opens
  useEffect(() => {
    if (isDialogOpen && processId) {
      setCheckingAuthor(true);
      const loadEpilogue = async () => {
        try {
          const response = await retryFetch(
            `${config.API_BASE}/api/processes/${processId}`,
            {
              method: "GET",
              credentials: "include",
            },
          );

          if (response.ok) {
            const processData = await response.json();
            const loadedEpilogue = processData.epilogue || "";
            setEpilogue(loadedEpilogue);
            prevEpilogueRef.current = loadedEpilogue;
            setEpilogueRecordings({
              ...(processData.epilogueRecordings || {}),
              image: processData.epilogueImage ?? undefined,
            });

            setIsMarkedAsExample(!!processData.isExample);

            // Extract user data for AI extraction
            // Priority: fullUserData (flat map) > aggregated from stepStack
            let extractedUserData: Record<string, any> = {};
            if (
              processData.fullUserData &&
              typeof processData.fullUserData === "object" &&
              Object.keys(processData.fullUserData).length > 0
            ) {
              extractedUserData = { ...processData.fullUserData };
            } else if (Array.isArray(processData.stepStack)) {
              // Fallback: aggregate from stepStack
              for (const step of processData.stepStack) {
                if (
                  step.activity?.startsWith("CURRENT_SCREEN|") ||
                  step.activity === "TERMINATE" ||
                  step.activity?.startsWith("REOPEN|")
                )
                  continue;
                if (step.userData && typeof step.userData === "object") {
                  Object.assign(extractedUserData, step.userData);
                }
              }
            }

            const hasUserData = Object.keys(extractedUserData).length > 0;

            // Get diagramJSON - either from populated fiPrescriptionHistory or fetch it
            if (hasUserData) {
              try {
                let diagramJSONStr: string | null = null;
                let presLanguage = "en";

                const presHistory = processData.fiPrescriptionHistory;
                if (presHistory && typeof presHistory === "object" && presHistory.diagramJSON) {
                  // fiPrescriptionHistory is populated
                  diagramJSONStr = presHistory.diagramJSON;
                  presLanguage = presHistory.prescriptionLanguage || "en";
                } else {
                  // fiPrescriptionHistory is just an ID string - fetch it
                  const presHistoryId = typeof presHistory === "string" ? presHistory : presHistory?._id;
                  if (presHistoryId) {
                    const phResponse = await retryFetch(
                      `${config.API_BASE}/api/prescriptionhistory/${presHistoryId}`,
                      { method: "GET", credentials: "include" },
                    );
                    if (phResponse.ok) {
                      const phData = await phResponse.json();
                      diagramJSONStr = phData.diagramJSON;
                      presLanguage = phData.prescriptionLanguage || "en";
                    }
                  }
                }

                if (diagramJSONStr) {
                  const formatted = formatUserDataAsText(
                    extractedUserData,
                    diagramJSONStr,
                    presLanguage,
                  );
                  if (formatted) setFormattedUserData(formatted);
                }
              } catch (err) {
                Logger.error("Failed to format user data:", err);
              }
            }

            // Check if current user is the author of the activity
            if (processData.fiPrescription) {
              try {
                const prescriptionResponse = await retryFetch(
                  `${config.API_BASE}/api/prescriptions/${processData.fiPrescription}`,
                  {
                    method: "GET",
                    credentials: "include",
                  },
                );

                if (prescriptionResponse.ok) {
                  const prescriptionData = await prescriptionResponse.json();

                  // fiUser could be either an ID string or a populated user object
                  const authorId =
                    typeof prescriptionData.fiUser === "string"
                      ? prescriptionData.fiUser
                      : prescriptionData.fiUser?._id;

                  // Check if current user is the author by comparing IDs
                  const currentUserId = currentUser?._id;
                  setIsActivityAuthor(
                    !!(
                      currentUserId &&
                      authorId &&
                      String(currentUserId) === String(authorId)
                    ),
                  );

                }
              } catch (error) {
                Logger.error("Failed to load prescription:", error);
              }
            }
          }
        } catch (error) {
          Logger.error("Failed to load epilogue:", error);
        } finally {
          setCheckingAuthor(false);
        }
      };

      loadEpilogue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen, processId]);

  // Manual save function for epilogue
  const handleSaveEpilogue = async () => {
    if (!processId) return;

    try {
      setSavingEpilogue(true);
      Logger.debug("Saving epilogue to backend");
      await retryFetch(
        `${config.API_BASE}/api/processes/${processId}/epilogue`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ epilogue }),
        },
      );
      Logger.debug("Epilogue saved successfully");
      toast.success(t("epilogue_saved") || "Epilogue saved");
      prevEpilogueRef.current = epilogue;
    } catch (error) {
      Logger.error("Failed to save epilogue:", error);
      toast.error(t("failed_to_save_epilogue") || "Failed to save epilogue");
    } finally {
      setSavingEpilogue(false);
    }
  };

  // Navigate to Review page in edit mode for marking as example
  const handleReviewAndMarkAsExample = () => {
    setIsDialogOpen(false);
    navigate(`/review?pid=${processId}&editForExample=1`);
    toast.info(
      t("review_before_marking_example") ||
        "Review your engagement and make any changes before marking as example.",
      { duration: 5000 }
    );
  };

  const toggleNoteType = async (index: number) => {
    const note: any = notes[index];
    if (!note._id) return;

    const newType = note.type === "action" ? "insight" : "action";
    await editNote(note._id, { type: newType });
  };

  const deleteTakeAway = async (index: number) => {
    const note = notes[index];
    if (!note._id) return;

    await removeNote(note._id);
    if (selectedNoteIndex === index) {
      setSelectedNoteIndex(null);
    } else if (selectedNoteIndex !== null && selectedNoteIndex > index) {
      setSelectedNoteIndex(selectedNoteIndex - 1);
    }
  };

  const saveNoteContent = async (index: number, content: string) => {
    const note = notes[index];
    if (!note._id) return;

    if (content.trim() === "") {
      await removeNote(note._id);
    } else {
      await editNote(note._id, { content });
    }
  };

  const renderNotesList = (
    notesList: any,
    title: string,
    icon: React.ReactNode,
    emptyMessage: string,
  ) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-medium">{title}</h4>
          <span className="text-xs text-muted-foreground">
            ({notesList.length})
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            if (title === "Insights") {
              setNewNoteType("insight");
            } else {
              setNewNoteType("action");
            }
            setShowNewNoteForm(true);
          }}
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-1" />
          {t("add")}
        </Button>
      </div>
      <div className="space-y-2 max-h-[10rem] overflow-y-auto pr-1">
        {notesList.map((note: any) => {
          const noteIndex = notes.findIndex((n) => n._id === note._id);
          return (
            <div
              key={note._id}
              className="flex items-center gap-2 p-2 border rounded-md group hover:bg-muted/50"
            >
              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6 mt-1 shrink-0"
                onClick={() => toggleNoteType(noteIndex)}
                title={note.type === "action" ? "Action Item" : "Insight"}
                disabled={loading}
              >
                {note.type === "action" ? (
                  <CheckSquare className="w-4 h-4 text-primary" />
                ) : (
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                )}
              </Button>

              <div className="flex-1 space-y-2">
                {selectedNoteIndex === noteIndex ? (
                  <Textarea
                    defaultValue={note.content}
                    className="resize-none min-h-[60px]"
                    autoFocus
                    onBlur={(e) => {
                      setSelectedNoteIndex(null);
                      saveNoteContent(noteIndex, e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        setSelectedNoteIndex(null);
                        saveNoteContent(noteIndex, e.currentTarget.value);
                      }
                    }}
                    disabled={loading}
                  />
                ) : (
                  <div
                    className="text-sm cursor-pointer"
                    onClick={() => setSelectedNoteIndex(noteIndex)}
                  >
                    {note.content}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6 mt-1 transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
                onClick={() => deleteTakeAway(noteIndex)}
                title="Delete note"
                disabled={loading}
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          );
        })}

        {notesList.length === 0 && (
          <div className="py-3 text-center text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );

  const copyTakeawaysToClipboard = async () => {
    const actionItems = notes
      .filter(
        (note: any) => note.type === "action" && note.content.trim() !== "",
      )
      .map((note: any) => `- ${note.content}`);

    const insights = notes
      .filter(
        (note: any) => note.type === "insight" && note.content.trim() !== "",
      )
      .map((note: any) => `- ${note.content}`);

    let notesText = "";

    if (actionItems.length > 0) {
      notesText += `📝 Action Items:\n${actionItems.join("\n")}\n\n`;
    }

    if (insights.length > 0) {
      notesText += `💡 Insights:\n${insights.join("\n")}`;
    }

    if (!notesText.trim()) {
      toast.warning("No notes to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(notesText.trim());
      toast.info("Copied Take-Aways to Clipboard!");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy notes");
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) return;

    const success = await addNote({
      type: newNoteType,
      content: newNoteContent,
      createdBy: "current-user",
    });

    if (success) {
      setNewNoteContent("");
      setShowNewNoteForm(false);
    }
  };

  // Format user data from the activity as Q&A text for AI extraction
  const formatUserDataAsText = (
    fullUserData: Record<string, any>,
    diagramJSON: string | object,
    language: string,
  ): string => {
    // convertPrescription expects a JSON string; handle already-parsed objects
    const jsonStr =
      typeof diagramJSON === "string"
        ? diagramJSON
        : JSON.stringify(diagramJSON);
    const diagram = convertPrescription(jsonStr);
    const lines: string[] = [];

    for (const [activityId, nodeData] of Object.entries(diagram.data)) {
      const node = nodeData as any;
      const activityLabel =
        node.label?.[language] || node.label?.en || "Activity";
      const activityLines: string[] = [];

      let customData = node.custom;
      if (customData && !Array.isArray(customData)) {
        customData = convertOldCustomToNew(customData);
      }

      if (Array.isArray(customData)) {
        for (const component of customData) {
          const fieldId = component.content?.key || component.content?.id;
          if (!fieldId) continue;

          const value = fullUserData[fieldId];
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "object" && Object.keys(value).length === 0)
          )
            continue;

          const question =
            component.content?.label ||
            component.content?.placeholder ||
            fieldId;

          // Format the value based on its type
          let formattedValue: string;
          if (typeof value === "string") {
            formattedValue = value;
          } else if (typeof value === "object") {
            // Handle structured data (e.g., SmartList items, CategoriesBoard)
            if (value.items && Array.isArray(value.items)) {
              formattedValue = value.items
                .map((item: any) => item.text || item.content || String(item))
                .join(", ");
            } else if (value.categories && value.categoryItems) {
              // Categories board: flatten to readable format
              const categories = value.categories as Array<{ id: number | string; title: string }>;
              const categoryItems = value.categoryItems as Record<string | number, Array<{ text: string }>>;
              const lines: string[] = [];

              categories.forEach((cat) => {
                const items = categoryItems[cat.id];
                if (items && items.length > 0) {
                  lines.push(`${cat.title}: ${items.map(i => i.text).join(', ')}`);
                }
              });

              formattedValue = lines.join('; ');
            } else {
              formattedValue = JSON.stringify(value);
            }
          } else {
            formattedValue = String(value);
          }

          // Skip non-meaningful values
          if (
            formattedValue.trim().length === 0 ||
            formattedValue === "1" ||
            formattedValue === "{}"
          )
            continue;

          activityLines.push(`Q: ${question}\nA: ${formattedValue}`);
        }
      }

      if (activityLines.length > 0) {
        lines.push(`--- ${activityLabel} ---`);
        lines.push(...activityLines);
        lines.push("");
      }
    }

    return lines.join("\n").trim();
  };

  // Handle AI extraction completion - add extracted items as notes
  const handleExtractComplete = async (items: ExtractedItem[]) => {
    let addedCount = 0;
    for (const item of items) {
      const result = await addNote({
        type: item.type,
        content: item.content,
        createdBy: "ai-extraction",
      });
      if (result) addedCount++;
    }
    if (addedCount > 0) {
      toast.success(
        t("extraction_added_count", {
          count: addedCount,
          defaultValue: `Added ${addedCount} items to your takeaways`,
        }),
      );
    }
  };

  const actionNotes = notes.filter((note: any) => note.type === "action");
  const insightNotes = notes.filter((note: any) => note.type === "insight");

  function computeForwardLocationURL() {
    if (forwardLocation.pathname && forwardLocation.search) {
      return `${forwardLocation.pathname}${forwardLocation.search}`;
    } else {
      return forwardLocation.pathname;
    }
  }

  const continueTo = computeForwardLocationURL();

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto overflow-x-hidden md:max-w-2xl xl:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("engagement_completed_modal_title")}!</DialogTitle>
        </DialogHeader>

        <div className="w-full min-w-0 space-y-4">
          {isUserSignedIn ? (
            <div className="space-y-3 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
                <CheckSquare className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="mb-1 font-medium">
                  {t("engagement_saved_heading")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("engagement_saved_description")}
                </p>
              </div>
              {/* AI Extraction CTA */}
              {formattedUserData && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowExtractorDialog(true)}
                        className="text-muted-foreground hover:text-violet-700 hover:bg-violet-50"
                      >
                        <Sparkles className="w-3.5 h-3.5 me-1.5" />
                        {t("extract_with_ai_button", "Extract with AI")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>
                        {t(
                          "extract_insights_description",
                          "AI can find action items and insights from your responses",
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {notes.filter((note) => note.content.trim() !== "").length >
                0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      {t("your_takeaways")}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyTakeawaysToClipboard}
                      className="flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {t("copy_all_notes")}
                    </Button>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Actions Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          actionNotes,
                          t("action_items"),
                          <CheckSquare className="w-4 h-4 text-primary" />,
                          t("no_action_items_yet"),
                        )}
                    </div>

                    {/* Insights Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          insightNotes,
                          t("insights"),
                          <Lightbulb className="w-4 h-4 text-amber-500" />,
                          t("no_insights_yet"),
                        )}
                    </div>
                  </div>

                  {/* New Note Form */}
                  {showNewNoteForm && (
                    <div className="p-3 space-y-3 border rounded-md bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 shrink-0"
                          onClick={() => {
                            setNewNoteType(
                              newNoteType === "action" ? "insight" : "action",
                            );
                          }}
                        >
                          {newNoteType === "action" ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                          )}
                        </Button>
                        <div className="text-sm font-medium">
                          {newNoteType === "action"
                            ? t("action_item")
                            : t("insight")}
                        </div>
                      </div>

                      <Textarea
                        placeholder={t("new_note_field_placeholder")}
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        className="resize-none min-h-[80px]"
                        autoFocus
                      />

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewNoteForm(false);
                            setNewNoteContent("");
                          }}
                          disabled={loading}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNote}
                          disabled={!newNoteContent.trim() || loading}
                        >
                          {loading ? t("saving") : t("save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Epilogue Section */}
              <div className="pt-6 space-y-2 border-t">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("epilogue")}
                  </h3>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("epilogue_description") ||
                    "Reflect on what you learned or accomplished during this engagement"}
                </p>
                <MinimalEditor
                  value={epilogue}
                  onChange={setEpilogue}
                  placeholder={
                    t("epilogue_placeholder") ||
                    "What did you learn? What insights did you gain? What would you do differently next time?"
                  }
                  recordings={epilogueRecordings}
                  onRecordingsChange={setEpilogueRecordings}
                  recordingContext="epilogue"
                  processId={processId}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleSaveEpilogue}
                    disabled={
                      savingEpilogue || epilogue === prevEpilogueRef.current
                    }
                  >
                    {savingEpilogue
                      ? t("saving") || "Saving..."
                      : t("save") || "Save"}
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 mt-6 sm:flex-row">
                {isMarkedAsExample ? (
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none sm:min-w-[140px] border-green-300 bg-green-50 text-green-800 hover:bg-green-100 hover:text-green-900"
                    disabled
                  >
                    {t("marked_as_example") || "Marked as example"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleReviewAndMarkAsExample}
                    className="flex-1 sm:flex-none sm:min-w-[140px]"
                    disabled={checkingAuthor}
                  >
                    {checkingAuthor ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Edit3 className="w-4 h-4 mr-2" />
                    )}
                    {checkingAuthor
                      ? t("loading") || "Loading..."
                      : isActivityAuthor
                        ? t("review_and_mark_as_example") ||
                          "Review & Mark as example"
                        : t("review_and_propose_as_example") ||
                          "Review & Propose as example"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowReminderModal(true)}
                  className="flex-1 sm:flex-none sm:min-w-[140px]"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {t("set_reminder")}
                </Button>
                <Link to={continueTo} className="flex-1">
                  <Button className="w-full">{t("continue")}</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-yellow-100 rounded-full">
                <Info className="w-8 h-8 text-yellow-600" />
              </div>
              <div>
                <p className="mb-1 font-medium text-yellow-800">
                  {t("engagement_completed_heading_for_non_logged_in_user")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("engagement_completed_description_for_non_logged_in_user")}
                </p>
              </div>
              {notes.filter((note) => note.content.trim() !== "").length >
                0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      {t("your_takeaways")}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyTakeawaysToClipboard}
                      className="flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {t("copy_all_notes")}
                    </Button>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Actions Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          actionNotes,
                          t("action_items"),
                          <CheckSquare className="w-4 h-4 text-primary" />,
                          t("no_action_items_yet"),
                        )}

                      {!showNewNoteForm && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setNewNoteType("action");
                            setShowNewNoteForm(true);
                          }}
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t("add_action_item")}
                        </Button>
                      )}
                    </div>

                    {/* Insights Section */}
                    <div className="space-y-3">
                      {!showNewNoteForm &&
                        renderNotesList(
                          insightNotes,
                          t("insights"),
                          <Lightbulb className="w-4 h-4 text-amber-500" />,
                          t("no_insights_yet"),
                        )}

                      {!showNewNoteForm && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setNewNoteType("insight");
                            setShowNewNoteForm(true);
                          }}
                          disabled={loading}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t("add_insight")}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* New Note Form */}
                  {showNewNoteForm && (
                    <div className="p-3 space-y-3 border rounded-md bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 shrink-0"
                          onClick={() => {
                            setNewNoteType(
                              newNoteType === "action" ? "insight" : "action",
                            );
                          }}
                        >
                          {newNoteType === "action" ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                          )}
                        </Button>
                        <div className="text-sm font-medium">
                          {newNoteType === "action" ? "Action Item" : "Insight"}
                        </div>
                      </div>

                      <Textarea
                        placeholder="Enter your note here..."
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        className="resize-none min-h-[80px]"
                        autoFocus
                      />

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewNoteForm(false);
                            setNewNoteContent("");
                          }}
                          disabled={loading}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateNote}
                          disabled={!newNoteContent.trim() || loading}
                        >
                          {loading ? t("saving") : t("save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Epilogue Section */}
              <div className="pt-6 space-y-2 border-t">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("epilogue")}
                  </h3>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("epilogue_description") ||
                    "Reflect on what you learned or accomplished during this engagement"}
                </p>
                <MinimalEditor
                  value={epilogue}
                  onChange={setEpilogue}
                  placeholder={
                    t("epilogue_placeholder") ||
                    "What did you learn? What insights did you gain? What would you do differently next time?"
                  }
                  recordings={epilogueRecordings}
                  onRecordingsChange={setEpilogueRecordings}
                  recordingContext="epilogue"
                  processId={processId}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleSaveEpilogue}
                    disabled={
                      savingEpilogue || epilogue === prevEpilogueRef.current
                    }
                  >
                    {savingEpilogue
                      ? t("saving") || "Saving..."
                      : t("save") || "Save"}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Button className="w-full">{t("login")}</Button>
                <Button variant="outline" className="w-full">
                  {t("register_new_user")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Checkpoint Reminder Modal */}
      <CheckpointReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        processId={processId}
        activityTitle={activityTitle || "this activity"}
        notes={notes}
      />

      {/* AI Extractor Dialog */}
      <UnifiedExtractorDialog
        open={showExtractorDialog}
        onOpenChange={setShowExtractorDialog}
        onExtractComplete={handleExtractComplete}
        categories={[]}
        mode="both"
        initialText={formattedUserData}
      />

    </Dialog>
  );
};

export default ActivityCompletionModal;
